import { useEffect, useState, useRef } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Location } from '../types';

export interface CauseListStatsData {
  topTypes: { type: string; count: number }[];
  topHearing: { type: string; count: number }[];
  topYears: { year: string; count: number }[];
  topCourts: { court: string; count: number }[];
}

export interface CauseListMeta {
  id: string;
  date: string;
  parsedAt: number | null;
  totalCases: number;
  totalCourts: number;
  stats?: CauseListStatsData;
  courts?: { court: string; judgeName: string[]; totalCases: number }[];
  rawAnnouncements?: string;
  pdfUrl?: string;
}

function toMs(raw: unknown): number | null {
  if (!raw) return null;
  if (typeof (raw as any).toMillis === 'function') return (raw as any).toMillis();
  if (typeof raw === 'number') return raw;
  return null;
}

export function useCauseList(location: Location) {
  const [meta, setMeta] = useState<CauseListMeta | null>(null);
  const [searchIndex, setSearchIndex] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSearchIndex = async () => {
    if (!meta?.id || searchIndex.length > 0) return;
    try {
      const indexSnap = await getDoc(doc(db, 'causeLists', meta.id, 'searchIndex', 'index'));
      if (indexSnap.exists()) {
        setSearchIndex(indexSnap.data().data || []);
      }
    } catch (err) {
      console.error("Failed to fetch search index:", err);
    }
  };

  const lastLoadedRef = useRef<{ metaId: string; parsedAt: number | null } | null>(null);

  useEffect(() => {
    setLoading(true);
    setMeta(null);
    setSearchIndex([]);
    lastLoadedRef.current = null;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'causeLists'),
        where('location', '==', location),
        orderBy('date', 'desc'),
        limit(1)
      ),
      async (snap) => {
        if (snap.empty) {
          setMeta(null);
          setSearchIndex([]);
          setLoading(false);
          return;
        }

        const latestDoc = snap.docs[0];
        const metaId = latestDoc.id;
        const data = latestDoc.data();
        const newParsedAt = toMs(data.parsedAt);

        setMeta({
          id: metaId,
          date: data.date,
          parsedAt: newParsedAt,
          totalCases: data.totalCases ?? 0,
          totalCourts: data.totalCourts ?? 0,
          stats: data.stats,
          courts: data.courts,
          rawAnnouncements: data.rawAnnouncements,
          pdfUrl: data.pdfUrl,
        });

        const last = lastLoadedRef.current;
        if (last && last.metaId === metaId && last.parsedAt === newParsedAt) return;

        if (last && last.metaId !== metaId) {
          setSearchIndex([]);
        }

        lastLoadedRef.current = { metaId, parsedAt: newParsedAt };
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [location]);

  return { meta, searchIndex, fetchSearchIndex, loading, error };
}
